import { datadogRum } from '@datadog/browser-rum';
import { useMachine } from '@xstate/react';
import { IchiInsights } from 'common/models/Insights';
import { Treasury } from 'common/models/treasury';
import {
  MINTABLE_TOKENS,
  MintableToken,
  insufficientTokenMessage,
} from 'constants/contracts';
import { navigate } from 'gatsby';
import useParsedQueryString from 'hooks/useParsedQueryString';
import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { useDebounce, useDeepCompareEffect } from 'react-use';
import { RedeemEvent } from 'xstate/redeemEvent';
import {
  EventInputChangeArgs,
  EventSelectTokenToRedeemArgs,
  EventUpdateAccountInfoArgs,
  initialRedeemContext,
  redeemMachine,
  RedeemState,
} from 'xstate/redeemMachine';
import { XStateUtils } from 'xstate/xStateUtils';
import { AdminAction, AdminButton } from '../AdminButton';
import GeneralInput from '../GeneralInput/GeneralInput';
import SelectToken from '../SelectToken/SelectToken';
import SpinnerButton from '../SpinnerButton';
import RedeemMetadata from './RedeemMetadata';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { chains } from 'containers/WalletProvider/WalletProvider';

type Props = {
  treasuries: Treasury[];
  isLoadingTreasuries: boolean;
};

const Redeem: React.FC<Props> = (props) => {
  const { chainId } = useSelectedChain();
  const chain = chains.find((c) => c.id === chainId);
  const { address: accountAddress } = useAccount();
  const [mintableTokens, setMintableTokens] = useState(
    Object.values(MINTABLE_TOKENS[chainId])
  );

  useEffect(() => {
    setMintableTokens(Object.values(MINTABLE_TOKENS[chainId]));
  }, [chainId]);

  const [tokenToRedeem, setTokenToRedeem] = React.useState(
    initialRedeemContext.tokenContractContainer
  );

  const { name: urlName } = useParsedQueryString();
  const [amount, setAmount] = React.useState('');
  const [state, send] = useMachine(redeemMachine, {
    // Xstate has a bug where if dev tools are enabled and the context is even remotely complicated the
    // inspector is extrodinarily slow on stringify
    // https://github.com/davidkpiano/xstate/issues/2048
    // devTools: process.env.NODE_ENV === 'development'
  });

  const onChangeMachineAmount = (value: string) => {
    const args: EventInputChangeArgs = {
      inputValue: value,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      currentEthBalance: currentEthBalance as number,
    };
    send(RedeemEvent.INPUT_CHANGE, XStateUtils.createEventPayload(args));
  };

  useDebounce(
    () => {
      onChangeMachineAmount(amount);
    },
    350,
    [amount]
  );

  const onChangeAmount = (value: string) => {
    setAmount(value);
  };

  const getTreasuryByToken = (token: string): Treasury => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < props.treasuries.length; i++) {
      if (props.treasuries[i].name.toLowerCase() === token.toLowerCase()) {
        return props.treasuries[i];
      }
    }
    return props.treasuries[0];
  };

  // Udate the account info once it's set
  React.useEffect(() => {
    // Whenever the wallet is connected, update the account info in the mintMachine
    // which may trigger other states, like checking for existing transactions
    if (chainId && accountAddress) {
      const payload: EventUpdateAccountInfoArgs = {
        address: accountAddress,
        chainId: chainId,
      };
      send(
        RedeemEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, accountAddress, mintableTokens]);

  // // When the mintableTokens changes, select a token if necessary
  React.useEffect(() => {
    if (
      mintableTokens &&
      mintableTokens.length > 0 &&
      (!state.context.tokenContractContainer ||
        !mintableTokens.find(
          (c) =>
            c.token.symbol ===
            state.context.tokenContractContainer?.token.symbol
        ))
    ) {
      // by default just select the first one
      let tokenContractContainer = mintableTokens[0];
      // If the token name is in the url, override
      if (urlName) {
        const urlToken = mintableTokens.find((c) => c.token.symbol === urlName);
        tokenContractContainer = urlToken || tokenContractContainer;
      }

      if (tokenContractContainer && tokenContractContainer.token.symbol) {
        setTokenToRedeem(tokenContractContainer);
        const treasury = getTreasuryByToken(
          tokenContractContainer.token.symbol
        );
        if (treasury) {
          const payload: EventSelectTokenToRedeemArgs = {
            tokenContractContainer,
            redeemFee: treasury.withdrawFee,
          };
          send(
            RedeemEvent.SELECT_TOKEN_TO_REDEEM,
            XStateUtils.createEventPayload(payload)
          );
        }
      }

      if (state.context && !state.context.tokenContractContainer) {
        // Update the url if the token symbols don't match
        if (urlName !== tokenContractContainer.token.symbol) {
          // Handle updating the url with the collateral token, since there is only one right now, we only do this once.
          const urlQuery = qs.stringify({
            tab: 'redeem',
            name: tokenContractContainer.token.symbol,
          });
          void navigate(`/mint?${urlQuery}`);
        }
      }
    }
  }, [urlName, mintableTokens, props.treasuries, tokenToRedeem]);

  useDeepCompareEffect(() => {
    if (
      state.context.tokenContractContainer &&
      state.context.tokenContractContainer.token
    ) {
      if (state.context.tokenContractContainer.token.symbol !== urlName) {
        const urlQuery = qs.stringify({
          tab: 'redeem',
          name: state.context.tokenContractContainer.token.symbol,
        });
        void navigate(`/mint?${urlQuery}`);
      }
    }
  }, [state]);

  const onChangeTokenToRedeem = (c: MintableToken, logAction = true) => {
    setTokenToRedeem(c);
    if (c && c.token.symbol) {
      if (logAction === true) {
        datadogRum.addAction(
          IchiInsights.DataDogActions.Redeem.SelectToken(c.token.symbol)
        );
      }

      const treasury = getTreasuryByToken(c.token.symbol);
      if (treasury) {
        const payload: EventSelectTokenToRedeemArgs = {
          tokenContractContainer: c,
          redeemFee: treasury.withdrawFee,
        };
        send(
          RedeemEvent.SELECT_TOKEN_TO_REDEEM,
          XStateUtils.createEventPayload(payload)
        );
      }
    }
  };

  // console.log(`current state.value: ${state.value}; acc_address: ${state.context.accountAddress}`);
  const noWalletConnected = state.matches(RedeemState.NO_WALLET_CONNECTED);
  const insufficientOneToken = state.matches(
    RedeemState.INSUFFICIENT_ONE_TOKEN
  );
  const tooManyDecimals = state.matches(RedeemState.TOO_MANY_DECIMALS);
  const redeeming = state.matches(RedeemState.REDEEMING);
  const redeemingError = state.matches(RedeemState.REDEEMING_ERROR);
  const redeemingSuccess = state.matches(RedeemState.REDEEMING_SUCCESS);
  const isLoadingRedeemData = state.matches(RedeemState.LOADING_DATA);

  const mergedMeta = XStateUtils.mergeMeta(state.meta);
  const currentEthBalance = 0;

  const isButtonDisabled =
    redeeming || insufficientOneToken || !amount || tooManyDecimals;
  const isMetadataDisplayed = amount ? +amount > 0 : false;

  const isProcessingInput = amount !== state.context.amount;
  const isRdeemingDisabled =
    isProcessingInput ||
    state.context.isRedeeming ||
    state.context.isLoadingData ||
    state.context.isResettingOneToken ||
    noWalletConnected;

  const adminActions: AdminAction[] = [
    {
      text: 'Reset One Token Allowance',
      onClick: () => send(RedeemEvent.RESET_ONE_TOKEN_ALLOWANCE),
    },
    {
      text: 'Approve One Token Allowance',
      onClick: () => send(RedeemEvent.APPROVE_ONE_TOKEN_ALLOWANCE),
    },
  ];
  return (
    <div className="flex flex-col mt-2">
      <div className="grid p-3 w-full">
        <SelectToken
          name="one-token"
          values={mintableTokens || []}
          isLoading={props.isLoadingTreasuries}
          value={tokenToRedeem as MintableToken}
          onChange={onChangeTokenToRedeem}
          id="oneTokenRedeem"
        />
        <GeneralInput
          isNumber={true}
          label="Enter Amount"
          value={amount}
          onChange={onChangeAmount}
        />
        {/* <InputError inputError={inputError} isLinkable={isInsufficientMember} tradeUrl={tradeUrl} /> */}
        {noWalletConnected && (
          // eslint-disable-next-line @typescript-eslint/dot-notation
          // <div className="error">{'Please connect a wallet!'}</div>
          <div className="error">
            {state.meta[`${redeemMachine.key}.${state.value}`].message}
          </div>
        )}
        {tooManyDecimals && (
          // eslint-disable-next-line @typescript-eslint/dot-notation
          <div className="error">{mergedMeta['message']}</div>
        )}
        {insufficientOneToken && (
          <div className="error">
            {/* eslint-disable-next-line @typescript-eslint/dot-notation */}
            {insufficientTokenMessage(
              state.context.tokenContractContainer?.token.name
            )}
          </div>
        )}
        {redeemingError && (
          <div className="error">
            <span>{state.context.redeemError?.message}</span>
          </div>
        )}
        {redeemingSuccess && (
          <div className="primary-bg-color white-color success">
            {state.context.redeemResponse?.output}
          </div>
        )}
      </div>
      <RedeemMetadata
        display={isMetadataDisplayed}
        isLoading={isLoadingRedeemData}
        // TODO: I don't like the difference in naming as it relates to semantics here
        redeemFee={state.context.redeemDataResponse?.redeemFee}
        // toCollect={state.context.redeemDataResponse?.collateralTokenUsd}
        toCollect={state.context.redeemDataResponse?.collateralTokenBN}
        collateralName={
          state.context.tokenContractContainer?.token.symbol === 'onegiv'
            ? 'DAI'
            : 'USDC'
        }
      />
      <div className="footer-bg-color flex justify-center items-center h-24 box-footer w-full">
        {/* eslint-disable-next-line no-nested-ternary */}
        {state.context.isOneTokenApproved === false ? (
          <SpinnerButton
            className="primary-button-bg-color"
            isLoading={
              state.context.isApprovingOneToken ||
              state.context.isLoadingData ||
              state.context.isResettingOneToken
            }
            // disabled={state.context.isApprovingOneToken || state.context.isLoadingData || state.context.isResettingOneToken}
            disabled={isButtonDisabled}
            onClick={() => send(RedeemEvent.APPROVE_ONE_TOKEN_ALLOWANCE)}
          >
            Approve {/* state.context.tokenContractContainer?.token?.name */}
          </SpinnerButton>
        ) : (
          <SpinnerButton
            className="primary-button-bg-color"
            isLoading={
              state.context.isRedeeming ||
              state.context.isLoadingData ||
              state.context.isResettingOneToken
            }
            disabled={isButtonDisabled || isRdeemingDisabled}
            data-dd-action-name={`Redeem ${state.context.tokenContractContainer?.token.symbol}`}
            onClick={() => send(RedeemEvent.REDEEM)}
          >
            Redeem
          </SpinnerButton>
        )}
        {chain?.testnet && <AdminButton actions={adminActions} />}
      </div>
    </div>
  );
};

export default Redeem;
